<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { store } from '@/store';
import { Bridge } from '@/common/bridge';
import { getParentBridgeMode, PARENT_BRIDGE_MODES } from '@/common/bridge/mode';
import * as CommsService from '@/common/bridge/modern-bridge/comms.service';
import { CommsCommand } from '@/common/bridge/modern-bridge/comms.enum';
import { isEmailValid, isPhoneValid } from '@/common/utils';
import * as rule from '@/common/lib/form-valid';
import type { LoginResult } from '@/common/types';
import ValidationInput from '@/components/ValidationInput.vue';
import type { ValidationInputCustomisation } from '@/components/ValidationInput.types';
import PlsButton1 from '@/components/PlsButton1.vue';
import LoadingIcon from '@/components/icons/LoadingIcon.vue';
import { useI18n } from '@/plugins/i18n';
import { analytics } from '@/common/logs/EventLogger';
import { BTN_EVTS } from '@/directives/trackClick';
import { useLoginFormAllCustomisation } from '../composables/login/useLoginFormCustomisation';
import {
  combineStyles,
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  resolveButtonStateColor,
} from '@/common/customisation/utils/styleUtils';
import vTrackClick from '@/directives/trackClick';
import { useBotUser } from '@/stores/useBotUser';
import { useConfig } from '@/stores/useConfig';
import { PleasedClient } from '@/pleased-sdk/PleasedClient';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

interface LoginForm {
  username: string;
  password: string;
}

/**
 * Context passed to `validateForm` for login field messages (see `rule.c` / `formatString`).
 * Must satisfy `rule.c`’s `CTX extends Record<...>` constraint.
 */
type LoginFormValidationCtx = Record<string, string | number | { toString: () => string }> & {
  uFieldType: string;
  uFieldName: string;
};

const fieldErrorMessage = (value: string | string[] | undefined): string => {
  if (value == null || value === '') {
    return '';
  }
  return Array.isArray(value) ? value.join('\n') : value;
};

interface Props {
  isGuestAllowed?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'on-login'): void;
  (e: 'continue-as-guest'): void;
  (e: 'cancel-support-request'): void;
}>();

const { t } = useI18n();

const loginCustomisation = useLoginFormAllCustomisation();
const {
  bodyBackground,
  title,
  apiErrorBanner,
  identifierInput,
  passwordInput,
  submitButton,
  guestButton,
  cancelButton,
} = loginCustomisation;

const botUserStore = useBotUser();
const { guestFilledDetails } = storeToRefs(botUserStore);

const { config } = storeToRefs(useConfig());

const loginType = computed<'external' | 'hybrid' | 'none'>(() => {
  const raw = config.value?.loginMethod ?? 'hybrid';
  const method = raw === 'external' || raw === 'hybrid' || raw === 'none' ? raw : 'hybrid';
  if (method === 'none' && !props.isGuestAllowed) {
    return 'hybrid';
  }
  return method;
});

const autoGuestEmitted = ref(false);

const tryEmitContinueAsGuestForNoneLogin = () => {
  if (autoGuestEmitted.value) {
    return;
  }
  if (loginType.value !== 'none' || !props.isGuestAllowed) {
    return;
  }
  autoGuestEmitted.value = true;
  nextTick(() => {
    emit('continue-as-guest');
  });
};

const usernameFieldType = computed(() => store.state.user.usernameFieldType as 'phone' | 'email');

const loading = ref(false);
const loginErrorFromApi = ref(false);

const form = reactive<LoginForm>({
  username: '',
  password: '',
});

const error = reactive({
  username: '',
  password: '',
});

const userFieldType = computed(() => {
  return usernameFieldType.value === 'email'
    ? {
        type: 'email' as const,
        name: t('global.email'),
        inputType: 'email' as const,
      }
    : {
        type: 'phone' as const,
        name: t('page.login.mobile.placeholder', 'Mobile no'),
        inputType: 'tel' as const,
      };
});

// TODO: add flag similar to !guestFilledDetails.value to not show "continue as guest" for AI case with login
const showGuestLoginOption = computed(() => {
  return Boolean(props.isGuestAllowed) && guestButton.isVisible.value;
});

const identifierPlaceholder = computed(() => {
  const custom = identifierInput.placeholder.value?.trim();
  if (custom) {
    return custom.startsWith('*') ? custom : `* ${custom}`;
  }
  return `* ${userFieldType.value.name}`;
});

const passwordPlaceholder = computed(() => {
  const custom = passwordInput.placeholder.value?.trim();
  if (custom) {
    return custom.startsWith('*') ? custom : `* ${custom}`;
  }
  return `* ${t('global.password', 'Password')}`;
});

const identifierCustomisation = computed((): ValidationInputCustomisation => {
  const hasError = Boolean(error.username);
  return {
    backgroundColor: identifierInput.backgroundColor.value ?? undefined,
    textColor: identifierInput.textColor.value ?? undefined,
    placeholderColor: identifierInput.placeholderColor.value ?? undefined,
    borderColor: hasError
      ? (identifierInput.errorBorderColor.value ?? identifierInput.borderColor.value ?? undefined)
      : (identifierInput.borderColor.value ?? undefined),
    errorColor: identifierInput.errorBorderColor.value ?? undefined,
  };
});

const passwordCustomisation = computed((): ValidationInputCustomisation => {
  const hasError = Boolean(error.password);
  return {
    backgroundColor: passwordInput.backgroundColor.value ?? undefined,
    textColor: passwordInput.textColor.value ?? undefined,
    placeholderColor: passwordInput.placeholderColor.value ?? undefined,
    borderColor: hasError
      ? (passwordInput.errorBorderColor.value ?? passwordInput.borderColor.value ?? undefined)
      : (passwordInput.borderColor.value ?? undefined),
    errorColor: passwordInput.errorBorderColor.value ?? undefined,
  };
});

const titleDisplay = computed(() => {
  const custom = title.titleText.value?.trim();
  return custom || t('page.login.fill');
});

const titleStyle = computed(() => createTextStyle(title.titleTextColor));

const apiErrorText = computed(() => t('page.login.invalid'));

const apiErrorBannerStyle = computed(() =>
  combineStyles(
    createBackgroundStyle(apiErrorBanner.backgroundColor),
    createBorderStyle(apiErrorBanner.borderColor),
    createTextStyle(apiErrorBanner.textColor)
  )
);

const rootStyle = computed(() => {
  if (!bodyBackground.isVisible.value) {
    return {};
  }

  const bgColor = bodyBackground.backgroundColor.value;
  return bgColor ? { backgroundColor: bgColor } : {};
});

const loginFormSuite = computed((): rule.ValidationSuite<LoginForm, LoginFormValidationCtx> => {
  return {
    username: [
      rule.c(rule.required, '{uFieldName} is required'),
      rule.c((val: string, _form: LoginForm, ctx?: LoginFormValidationCtx) => {
        if (ctx?.uFieldType === 'phone') {
          return isPhoneValid(val);
        }
        return isEmailValid(val);
      }, 'Please enter a valid {uFieldName}'),
    ],
    password: [rule.c(rule.required, 'Password is required')],
  };
});

const submitButtonText = computed(() => {
  const custom = submitButton.buttonText.value?.trim();
  return custom || t('page.login.button.login', 'Login');
});

const goToParentLoginButtonText = computed(() => t('page.login.button.go_to_login', 'Go to Login'));

const isSubmitDisabled = computed(() => loading.value);

const submitButtonBackgroundColor = computed(() =>
  resolveButtonStateColor(
    isSubmitDisabled.value,
    submitButton.disabledButtonBackgroundColor.value,
    submitButton.buttonBackgroundColor.value
  )
);

const submitButtonBorderColor = computed(() =>
  resolveButtonStateColor(
    isSubmitDisabled.value,
    submitButton.disabledButtonBorderColor.value,
    submitButton.buttonBorderColor.value
  )
);

const submitButtonTextColor = computed(() =>
  resolveButtonStateColor(
    isSubmitDisabled.value,
    submitButton.disabledButtonTextColor.value,
    submitButton.buttonTextColor.value
  )
);

const guestLinkText = computed(() => {
  const custom = guestButton.buttonText.value?.trim();
  return custom || t('page.login.continue_as_guest', 'Continue As Guest');
});

const guestButtonStyle = computed(() =>
  combineStyles(
    createBackgroundStyle(guestButton.buttonBackgroundColor),
    createBorderStyle(guestButton.buttonBorderColor),
    createTextStyle(guestButton.buttonTextColor)
  )
);

const cancelButtonText = computed(() => {
  const custom = cancelButton.buttonText.value?.trim();
  return custom || (t('global.cancel', 'Cancel') as string);
});

const cancelButtonStyle = computed(() =>
  combineStyles(
    createBackgroundStyle(cancelButton.buttonBackgroundColor),
    createBorderStyle(cancelButton.buttonBorderColor),
    createTextStyle(cancelButton.buttonTextColor)
  )
);

const passwordFieldErrorText = computed(() => {
  if (!error.password) {
    return '';
  }
  return `${t('page.login.password.empty')} ${error.password}`.trim();
});

let cleanPopListener: CallableFunction | null = null;

const handleIdentifierFocus = () => {
  error.username = '';
};

const handlePasswordFocus = () => {
  error.password = '';
};

const handleIdentifierInput = (value: string) => {
  form.username = value;
};

const handlePasswordInput = (value: string) => {
  form.password = value;
};

const onLoginSuccess = async (data: LoginResult) => {
  if (data.bizCode !== 10000) {
    loginErrorFromApi.value = true;
    return;
  }

  try {
    await store.dispatch('user/sportyLoginSuccess', data);
    const pleasedUser = store.state.user.pleasedUser;
    const hasLoggedInPleasedProfile = !!pleasedUser && !pleasedUser.isGuest && !!(pleasedUser.id || pleasedUser.token);

    if (!hasLoggedInPleasedProfile) {
      loginErrorFromApi.value = true;
      captureEventException(
        new Error('Login succeeded on host but Pleased profile was missing'),
        ErrorTrackingEvent.WidgetAuth
      );
      return;
    }

    botUserStore.setCurrentUser(pleasedUser);
    if (pleasedUser.token) {
      PleasedClient.setConfig({ token: pleasedUser.token });
    }
    emit('on-login');
  } catch (e) {
    loginErrorFromApi.value = true;
    captureEventException(e, ErrorTrackingEvent.WidgetAuth);
  }
};

const handleSubmit = async () => {
  loginErrorFromApi.value = false;
  loading.value = true;

  const { valid, errors } = await rule.validateForm(form, loginFormSuite.value, {
    uFieldType: userFieldType.value.type,
    uFieldName: userFieldType.value.name,
  });

  error.username = fieldErrorMessage(errors.username);
  error.password = fieldErrorMessage(errors.password);

  analytics.trackClick({
    name: BTN_EVTS.CONNECT_REQUEST_LOGIN_CLICKED,
    metadata: { valid, errors },
  });

  if (!valid) {
    loading.value = false;
    return;
  }

  try {
    const data = await Bridge.login({ username: form.username, password: form.password });
    await onLoginSuccess(data);
  } catch (error: unknown) {
    loginErrorFromApi.value = true;
    captureEventException(error, ErrorTrackingEvent.WidgetAuth);
  } finally {
    loading.value = false;
  }
};

const handleContinueAsGuest = () => {
  emit('continue-as-guest');
};

const handleGoToExternalLogin = () => {
  analytics.trackClick({
    name: BTN_EVTS.CONNECT_REQUEST_LOGIN_GO_TO_PARENT,
    metadata: { loginMethod: 'external' },
  });
  if (getParentBridgeMode() === PARENT_BRIDGE_MODES.MODERN_SDK) {
    CommsService.post(CommsCommand.emit.requestLogin);
    return;
  }
  Bridge.sendToParent('LOGIN_POP');
};

const handleCancelSupportRequest = () => {
  emit('cancel-support-request');
};

const handleCancelSupportRequestKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  handleCancelSupportRequest();
};

const handleGuestKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  handleContinueAsGuest();
};

onMounted(() => {
  tryEmitContinueAsGuestForNoneLogin();
  cleanPopListener = Bridge.listenFromParent('LOGIN_POP', async (data) => {
    loading.value = true;
    await onLoginSuccess(data);
    loading.value = false;
  });
});

onBeforeUnmount(() => {
  cleanPopListener?.();
});
</script>

<template>
  <div class="shadow-hard theme-font-main widget-login" :style="rootStyle" role="region" :aria-label="titleDisplay">
    <div class="widget-login__form">
      <template v-if="loginType !== 'none'">
        <div class="widget-login__title-row">
          <div class="theme-font-h4 widget-login__title" :style="titleStyle">
            {{ titleDisplay }}
          </div>
          <div v-if="loginErrorFromApi" class="theme-font-subtext widget-login__api-error" :style="apiErrorBannerStyle">
            <div class="widget-login__api-error-icon" aria-hidden="true">
              <img :src="require('@/assets/img/error.png')" alt="" />
            </div>
            <div>{{ apiErrorText }}</div>
          </div>
        </div>

        <template v-if="loginType === 'hybrid'">
          <div class="widget-login__field" @focusin="handleIdentifierFocus">
            <ValidationInput
              :value="form.username"
              :type="userFieldType.inputType"
              :placeholder="identifierPlaceholder"
              :show-error="Boolean(error.username)"
              :error-message="error.username"
              :customisation="identifierCustomisation"
              class="widget-login__validation-input"
              @input="handleIdentifierInput"
            />
          </div>

          <div class="widget-login__field" @focusin="handlePasswordFocus">
            <ValidationInput
              :value="form.password"
              type="password"
              :placeholder="passwordPlaceholder"
              :show-error="Boolean(error.password)"
              :error-message="passwordFieldErrorText"
              :customisation="passwordCustomisation"
              class="widget-login__validation-input"
              @input="handlePasswordInput"
            />
          </div>
        </template>

        <div v-if="loginType === 'hybrid' || loginType === 'external'" class="widget-login__actions">
          <button
            v-track-click="BTN_EVTS.CONNECT_REQUEST_LOGIN_CANCEL"
            type="button"
            class="theme-font-main widget-login__cancel"
            :style="cancelButtonStyle"
            :aria-label="cancelButtonText"
            tabindex="0"
            @click="handleCancelSupportRequest"
            @keydown="handleCancelSupportRequestKeyDown"
          >
            {{ cancelButtonText }}
          </button>
          <PlsButton1
            v-if="loginType === 'hybrid'"
            :aria-label="submitButtonText"
            :aria-busy="loading"
            :border-color="submitButtonBorderColor"
            :background-color="submitButtonBackgroundColor"
            :text-color="submitButtonTextColor"
            :disabled="isSubmitDisabled"
            class="widget-login__submit widget-login__submit--paired"
            @click="handleSubmit"
          >
            <span class="widget-login__submit-content">
              <LoadingIcon v-if="loading" class="widget-login__submit-spinner" aria-hidden="true" />
              <span class="widget-login__submit-label">{{ submitButtonText }}</span>
            </span>
          </PlsButton1>
          <PlsButton1
            v-else
            :aria-label="goToParentLoginButtonText"
            :border-color="submitButtonBorderColor"
            :background-color="submitButtonBackgroundColor"
            :text-color="submitButtonTextColor"
            class="widget-login__submit widget-login__submit--paired"
            @click="handleGoToExternalLogin"
          >
            <span class="widget-login__submit-label">{{ goToParentLoginButtonText }}</span>
          </PlsButton1>
        </div>

        <button
          v-if="showGuestLoginOption"
          v-track-click="BTN_EVTS.CONNECT_REQUEST_LOGIN_CONTINUE_AS_GUEST"
          type="button"
          class="theme-font-main widget-login__guest"
          :style="guestButtonStyle"
          :aria-label="guestLinkText"
          tabindex="0"
          @click="handleContinueAsGuest"
          @keydown="handleGuestKeyDown"
        >
          {{ guestLinkText }}
        </button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.widget-login {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px 16px;
}

.widget-login__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.widget-login__title-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  margin-bottom: 4px;
}

.widget-login__api-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 22.3rem;
  padding: 8px 10px;
  border-radius: 8px;
  box-sizing: border-box;
}

.widget-login__api-error-icon {
  height: 1.5rem;
  flex-shrink: 0;

  img {
    width: auto;
    height: 100%;
    display: block;
  }
}

.widget-login__field {
  width: 100%;
}

.widget-login__validation-input {
  width: 100%;
}

.widget-login__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 12px;
  margin-top: 4px;
}

.widget-login__cancel {
  margin: 0;
  flex: 1 1 calc(50% - 6px);
  min-width: min(36vw, 160px);
  max-width: calc(50% - 6px);
  padding: 12px 16px;
  cursor: pointer;
  text-align: center;
  border-radius: 8px;
  box-sizing: border-box;
  border: 2px solid transparent;
}

.widget-login__submit {
  min-width: min(36vw, 160px);
  flex: 1 1 calc(50% - 6px);
  max-width: calc(50% - 6px);
  padding: 12px 20px;
  box-sizing: border-box;
}

.widget-login__submit-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.widget-login__submit-spinner {
  flex-shrink: 0;
}

.widget-login__guest {
  margin: 0;
  margin-top: 4px;
  padding: 10px 14px;
  cursor: pointer;
  text-align: center;
  border-radius: 8px;
  box-sizing: border-box;
}
</style>
